import { moveToFulfilled, moveToPending, moveToRejected, update } from '@ms/yammer-data-support';
import { DiscoveryHiddenState } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import {
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadNewerThreadsPendingAction,
  FeedLoadNewerThreadsRejectedAction,
  FeedLoadOlderThreadsFulfilledAction,
  FeedLoadOlderThreadsPendingAction,
  FeedLoadOlderThreadsRejectedAction,
} from '../../domains/feedLoad/actions.types';
import {
  GroupDiscoverySettingsLoadFulfilledAction,
  GroupDiscoverySettingsLoadPendingAction,
  GroupDiscoverySettingsLoadRejectedAction,
  GroupHideForNetworkInDiscoveryFulfilledAction,
  GroupHideForViewerInDiscoveryFulfilledAction,
  GroupUnhideForNetworkInDiscoveryPendingAction,
  GroupUnhideForNetworkInDiscoveryRejectedAction,
  GroupUnhideForViewerInDiscoveryPendingAction,
  GroupUnhideForViewerInDiscoveryRejectedAction,
} from '../../domains/groupDiscoverySettings/actions.types';
import { GroupHeaderLoadAction } from '../../domains/groupHeaderLoad/actions.types';
import { Group } from '../groups/types';

import { GroupDiscoverySettings } from './types';

export type GroupDiscoverySettingsAction =
  | GroupHeaderLoadAction
  | GroupHideForNetworkInDiscoveryFulfilledAction
  | GroupUnhideForNetworkInDiscoveryPendingAction
  | GroupUnhideForNetworkInDiscoveryRejectedAction
  | GroupHideForViewerInDiscoveryFulfilledAction
  | GroupUnhideForViewerInDiscoveryPendingAction
  | GroupUnhideForViewerInDiscoveryRejectedAction
  | GroupDiscoverySettingsLoadPendingAction
  | GroupDiscoverySettingsLoadRejectedAction
  | GroupDiscoverySettingsLoadFulfilledAction
  | FeedLoadNewerThreadsPendingAction
  | FeedLoadNewerThreadsRejectedAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsPendingAction
  | FeedLoadOlderThreadsRejectedAction
  | FeedLoadOlderThreadsFulfilledAction;

const initialState: GroupDiscoverySettings = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const updateGroupDiscoverySettings = (
  state: GroupDiscoverySettings,
  groupId: Group['id'],
  hiddenForState: DiscoveryHiddenState,
  isHiddenForNetwork = true
) =>
  update(state, groupId, {
    ...state.fulfilled.byId[groupId],
    [isHiddenForNetwork ? 'hiddenForNetwork' : 'hiddenForViewer']: hiddenForState,
  });

const reducer: Reducer<GroupDiscoverySettings, GroupDiscoverySettingsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUP_HEADER.LOAD_PENDING':
      return moveToPending(state, [action.payload.groupId]);
    case 'GROUP_HEADER.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.groupId], action.payload.error);
    case 'GROUP_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupDiscoverySettings);
    case 'GROUP_DISCOVERY_SETTINGS.LOAD_PENDING':
      return moveToPending(state, [action.payload.groupId]);
    case 'GROUP_DISCOVERY_SETTINGS.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.groupId], action.payload.error);
    case 'GROUP_DISCOVERY_SETTINGS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupDiscoverySettings);
    case 'GROUP.HIDE_FOR_NETWORK_IN_DISCOVERY_FULFILLED':
      return updateGroupDiscoverySettings(state, action.payload.groupId, DiscoveryHiddenState.HIDDEN);
    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_PENDING':
      return updateGroupDiscoverySettings(state, action.payload.groupId, DiscoveryHiddenState.NOT_HIDDEN);
    case 'GROUP.UNHIDE_FOR_NETWORK_IN_DISCOVERY_REJECTED':
      return updateGroupDiscoverySettings(state, action.payload.groupId, DiscoveryHiddenState.HIDDEN);
    case 'GROUP.HIDE_FOR_VIEWER_IN_DISCOVERY_FULFILLED':
      return updateGroupDiscoverySettings(state, action.payload.groupId, DiscoveryHiddenState.HIDDEN, false);
    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_PENDING':
      return updateGroupDiscoverySettings(state, action.payload.groupId, DiscoveryHiddenState.NOT_HIDDEN, false);
    case 'GROUP.UNHIDE_FOR_VIEWER_IN_DISCOVERY_REJECTED':
      return updateGroupDiscoverySettings(state, action.payload.groupId, DiscoveryHiddenState.HIDDEN, false);
    default:
      return state;
  }
};

export default reducer;
